import { StepSchemas, type PropertyFormInput } from '../schemas/propertySchema'

export interface FormStep {
  title: string
  description: string
  schema: StepSchemas[keyof StepSchemas]
  required: boolean
}

/**
 * Step definitions for the property form wizard
 */
export const getFormSteps = (stepSchemas: StepSchemas): FormStep[] => [
  {
    title: 'Informações Básicas',
    description: 'Dados essenciais da propriedade',
    schema: stepSchemas.basicInfo,
    required: true,
  },
  {
    title: 'Detalhes da Propriedade',
    description: 'Características e preço',
    schema: stepSchemas.propertyDetails,
    required: true,
  },
  {
    title: 'Acesso e Instruções',
    description: 'Informações para hóspedes',
    schema: stepSchemas.accessInstructions,
    required: false,
  },
  {
    title: 'Custos e Gestão',
    description: 'Taxas e percentuais',
    schema: stepSchemas.costsManagement,
    required: true,
  },
  {
    title: 'Imagens',
    description: 'Fotos da propriedade',
    schema: stepSchemas.images,
    required: false,
  },
]

/**
 * Validate a specific step with the provided data
 */
export const validateFormStep = (
  stepIndex: number,
  formData: PropertyFormInput<boolean>,
  steps: FormStep[]
): boolean => {
  const step = steps[stepIndex]
  if (!step) return false

  try {
    step.schema.parse(formData)
    return true
  } catch {
    return false
  }
}

/**
 * Check if all required steps are valid
 */
export const areRequiredStepsValid = (
  formData: PropertyFormInput<boolean>,
  steps: FormStep[]
): boolean => {
  const requiredStepIndices = steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => step.required)
    .map(({ index }) => index)

  for (const stepIndex of requiredStepIndices) {
    const isValid = validateFormStep(stepIndex, formData, steps)
    if (!isValid) return false
  }

  return true
}

/**
 * Get the indices of required steps
 */
export const getRequiredStepIndices = (steps: FormStep[]): number[] => {
  return steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => step.required)
    .map(({ index }) => index)
}

/**
 * Check if a step can be navigated to (all previous required steps are valid)
 */
export const canNavigateToStep = (
  targetStepIndex: number,
  currentStepIndex: number,
  formData: PropertyFormInput<boolean>,
  steps: FormStep[]
): boolean => {
  // Can always go backwards
  if (targetStepIndex <= currentStepIndex) {
    return true
  }

  // For forward navigation, check if current step is valid (if required)
  const currentStep = steps[currentStepIndex]
  if (currentStep?.required) {
    return validateFormStep(currentStepIndex, formData, steps)
  }

  return true
}
